import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Layout from "@/components/Layout";
import { Radar } from "lucide-react";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import SearchPage from "@/pages/Search";
import Mapping from "@/pages/Mapping";
import Candidates from "@/pages/Candidates";
import Profile from "@/pages/Profile";
import ReviewQueue from "@/pages/ReviewQueue";
import Verification from "@/pages/Verification";
import AllowedUsers from "@/pages/AllowedUsers";
import AdminJobs from "@/pages/AdminJobs";
import Activity from "@/pages/Activity";

function Loader() {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-950">
      <Radar className="w-9 h-9 text-blue-400 radar-sweep" />
    </div>
  );
}

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/app/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/app/lookup" element={<Navigate to="/app/search" replace />} />
      <Route path="/app/search" element={<Protected><SearchPage /></Protected>} />
      <Route path="/app/mapping" element={<Protected><Mapping /></Protected>} />
      <Route path="/app/candidates" element={<Protected admin><Candidates /></Protected>} />
      <Route path="/app/ngo/*" element={<Protected><Profile /></Protected>} />
      <Route path="/app/review" element={<Protected admin><ReviewQueue /></Protected>} />
      <Route path="/app/verify" element={<Protected admin><Verification /></Protected>} />
      <Route path="/app/allowed-users" element={<Protected admin><AllowedUsers /></Protected>} />
      <Route path="/app/jobs" element={<Protected admin><AdminJobs /></Protected>} />
      <Route path="/app/activity" element={<Protected admin><Activity /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <TooltipProvider delayDuration={150}>
            <AppRouter />
          </TooltipProvider>
        </BrowserRouter>
        <Toaster position="top-right" theme="dark" richColors />
      </AuthProvider>
    </div>
  );
}
